import { ThemeProvider, createTheme } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createBrowserRouter,
  RouterProvider,
  RouteObject,
} from "react-router-dom";

import Login from "./pages/Login";
import Root from "./components/Root";
import Home from "./pages/Home";
import Orders from "./pages/Orders";
import FarmerOrders from "./pages/FarmerOrders";
import DeliveryRoute from "./pages/DeliveryRoute/index";
import AuthProvider from "./contexts/AuthContext";
import Report from "./pages/Report";
import Profile from "./pages/Profile";
import UploadCSVPage from "./pages/UploadCSVPage";
import ReconcilePage from "./pages/ReconcilePage";
import DashboardPage from "./pages/Dashboard";
import Fair from "./pages/Fair";
import CreateOrder from "./pages/CreateOrder";
import SeederOrders from "./pages/SeederOrders";
import CoreProductsList from "./pages/CoreProducts";
import CoreProductDetail from "./pages/CoreProductDetail";
import CoreProductEdit from "./pages/CoreProductEdit";
import CoreProductCreate from "./pages/CoreProductCreate";
import CoreProductCategories from "./pages/CoreProductCategories";
import FairOfferings from "./pages/FairOfferings";
import CreditTransactions from "./pages/CreditTransactions";
import FeatureFlagsProvider from "./contexts/FeatureFlagsContext";
import Farmers from "./pages/Farmers";
import PlatformUsageFees from "./pages/PlatformUsageFees";

const queryClient = new QueryClient();

// Feature flags configuration
// As variáveis de ambiente são lidas de .env e precisam começar com REACT_APP_
// Valores aceitos: 'true', '1' para habilitar; qualquer outro valor desabilita
const featureFlags = {
  CORE_PRODUCTS:
    process.env.REACT_APP_FEATURE_CORE_PRODUCTS === "true" ||
    process.env.REACT_APP_FEATURE_CORE_PRODUCTS === "1",
};

// Rotas de core-products (protegidas por feature flag)
const coreProductsRoutes: RouteObject[] = [
  {
    path: "core-products",
    element: <CoreProductsList />,
  },
  {
    path: "core-products/new",
    element: <CoreProductCreate />,
  },
  {
    path: "core-products/:id",
    element: <CoreProductDetail />,
  },
  {
    path: "core-products/:id/edit",
    element: <CoreProductEdit />,
  },
  {
    path: "core-product-categories",
    element: <CoreProductCategories />,
  },
];

// Rotas base da aplicação
const baseAppRoutes: RouteObject[] = [
  {
    element: <Orders />,
    path: "orders",
  },
  {
    element: <FarmerOrders />,
    path: "farmer-orders",
  },
  {
    element: <DeliveryRoute />,
    path: "delivery-route",
  },
  {
    element: <Report />,
    path: "report",
  },
  {
    element: <Profile />,
    path: "profile",
  },
  {
    element: <Fair />,
    path: "fair",
  },
  {
    element: <FairOfferings />,
    path: "fair-offerings",
  },
  {
    path: "finance/upload",
    element: <UploadCSVPage />,
  },
  {
    path: "finance/reconcile",
    element: <ReconcilePage />,
  },
  {
    path: "finance/dashboard",
    element: <DashboardPage />,
  },
  {
    path: "finance/platform-usage-fees",
    element: <PlatformUsageFees />,
  },
  {
    path: "create-order",
    element: <CreateOrder />,
  },
  {
    path: "seeder-orders",
    element: <SeederOrders />,
  },
  {
    path: "credit-transactions",
    element: <CreditTransactions />,
  },
  {
    path: "farmers",
    element: <Farmers />,
  },
];

// Construir rotas condicionalmente com base nas feature flags
const appRoutes: RouteObject[] = [
  ...baseAppRoutes,
  ...(featureFlags.CORE_PRODUCTS ? coreProductsRoutes : []),
];

const router = createBrowserRouter([
  {
    path: "login",
    element: <Login />,
  },
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "app",
    element: <Root />,
    children: appRoutes,
  },
]);

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#447737",
    },
    secondary: {
      main: "#9fd236",
      dark: "#82a221",
      light: "#d1ea76",
    },
    background: {
      default: "#f5f2e5",
      paper: "#ffffff",
    },
    text: {
      secondary: "#2f5326",
      disabled: "#2f5326",
    },
    success: {
      main: "#4ddc8c",
    },
    error: {
      main: "#f75e5e",
    },
    warning: {
      main: "#f79f57",
    },
    info: {
      main: "#6ebee8",
    },
    divider: "rgba(68,119,55,0.24)",
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <FeatureFlagsProvider flags={featureFlags}>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </FeatureFlagsProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
